<template>
  <div class="onboarding-finishing">
    <h1 class="onboarding-finishing__title" v-html="pageTitle"></h1>

    <div class="onboarding-finishing__progress">
      <div
        class="onboarding-finishing__progress-bar"
        :style="{
          '--current-progress': `${loadingProgress}%`,
        }"
      ></div>
      <p class="onboarding-finishing__progress-label">{{ loadingProgress }}%</p>
    </div>

    <div class="onboarding-finishing__steps">
      <Transition
        v-for="(step, index) in page.steps"
        :key="step.title"
        name="swap-state"
        mode="out-in"
      >
        <div
          :key="getStepTitle(step, index)"
          class="onboarding-finishing__step"
          :class="{
            'onboarding-finishing__step--prev':
              index < currentStepIndex || finishedLoading,
            'onboarding-finishing__step--current':
              index === currentStepIndex && !finishedLoading,
            'onboarding-finishing__step--next': index > currentStepIndex,
          }"
        >
          <div class="onboarding-finishing__step-dot"></div>

          <p class="onboarding-finishing__step-title">
            {{ getStepTitle(step, index) }}
          </p>

          <div
            v-if="index < currentStepIndex || finishedLoading"
            class="onboarding-finishing__status onboarding-finishing__status--prev"
          >
            <img
              class="onboarding-finishing__icon"
              src="@/assets.app/images/onboarding/check.svg"
              alt=""
            />
          </div>
          <FinishingSetupSpinner
            v-else-if="index === currentStepIndex"
            class="onboarding-finishing__status onboarding-finishing__status--current"
          />
          <div
            v-if="index > currentStepIndex"
            class="onboarding-finishing__status onboarding-finishing__status--next"
          ></div>
        </div>
      </Transition>
    </div>

    <div
      v-if="!view.isContinueButtonVisible"
      class="onboarding-finishing__reviews"
    >
      <img
        src="@/assets.app/images/onboarding/stars.svg"
        alt=""
        class="onboarding-finishing__stars"
      />
      <p class="onboarding-finishing__review">Enjoyed by 1M+ users</p>
    </div>

    <Teleport to="#appRoot">
      <Transition name="popup-pop">
        <div v-if="showQuestionPopup" class="onboarding-finishing__popup">
          <div class="onboarding-finishing__popup-inner">
            <p class="onboarding-finishing__popup-label">
              To continue, answer:
            </p>
            <p class="onboarding-finishing__popup-title">
              {{ currentQuestion }}
            </p>

            <div class="onboarding-finishing__popup-controls">
              <AppButton
                class="onboarding-finishing__popup-button onboarding-finishing__popup-button--no"
                :outlined="true"
                @click="submitAnswer(false)"
                >No</AppButton
              >
              <AppButton
                class="onboarding-finishing__popup-button onboarding-finishing__popup-button--yes"
                :outlined="true"
                @click="submitAnswer(true)"
                >Yes</AppButton
              >
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import { QuestionnaireView } from '@/models/onboarding'
import {
  FinishingSetupChoice,
  FinishingSetupPage,
  FinishingSetupPageSteps,
} from '@/models/onboarding.types'
import { PublicInterface } from '@/models/interfaces'

import FinishingSetupSpinner from '../FinishingSetupSpinner.vue'
import AppButton from '@/app/components/button/AppButton.vue'

const props = defineProps<{
  view: PublicInterface<QuestionnaireView>
}>()

const page = computed(() => {
  return props.view.currentPage as FinishingSetupPage
})

const pageTitle = computed(() => {
  if (finishedLoading.value) {
    return page.value.title
  }

  return page.value.titleLoading
})

const currentStepIndex = ref(0)
const currentQuestion = computed(() => {
  return page.value.steps[currentStepIndex.value].question
})

const finishedLoading = ref(false)

const showQuestionPopup = ref(false)

const nextQuestionThreshold = computed(() => {
  return (currentStepIndex.value + 1) * (100 / page.value.steps.length)
})

const currentChoice = computed(() => {
  return props.view.currentChoice as FinishingSetupChoice
})

function submitAnswer(answer: boolean): void {
  if (answer === true) {
    props.view.setCurrentChoice([...currentChoice.value, currentQuestion.value])
  }

  if (currentStepIndex.value + 1 === page.value.steps.length) {
    finishedLoading.value = true
    showQuestionPopup.value = false
    props.view.showContinueButton()
    return
  }

  showQuestionPopup.value = false
  startInterval()
  currentStepIndex.value++
}

function getStepTitle(
  step: FinishingSetupPageSteps,
  stepIndex: number,
): string {
  if (stepIndex >= currentStepIndex.value && !finishedLoading.value) {
    return step.titleLoading
  }

  return step.title
}

const updateProgressInterval = ref<number | null>(null)
const loadingProgress = ref(0)

function startInterval(): void {
  if (updateProgressInterval.value) {
    return
  }

  updateProgressInterval.value = setInterval(() => {
    loadingProgress.value += 2
  }, 150)

  showQuestionPopup.value = false
}

function stopInterval(): void {
  if (updateProgressInterval.value) {
    clearInterval(updateProgressInterval.value)
    updateProgressInterval.value = null
  }
}

watch(loadingProgress, (newValue) => {
  if (newValue >= nextQuestionThreshold.value) {
    stopInterval()
    showQuestionPopup.value = true
  }
})

onMounted(() => {
  // The default value of `view.currentChoice` is `undefined`.
  // The value of the choices for this page is an array, so
  // we set the initial value of `view.currentChoice` to an
  // empty array so that we can spread the current value
  // without having to check if it's an array first.
  props.view.setCurrentChoice([])
  startInterval()
})

onUnmounted(() => {
  stopInterval()
})
</script>

<style lang="scss" scoped>
.onboarding-finishing {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--onboarding-padding);
  padding-bottom: calc(var(--onboarding-padding) * 2);
  flex: 1;
  overflow-y: auto;

  &__title {
    width: 100%;
    max-width: var(--onboarding-outer-width);
    font-size: 28px;
    line-height: 36px;
    letter-spacing: -0.02em;
    color: $c-black-2;
    margin-bottom: 40px;
    font-weight: 500;
    @media (min-width: $w-small) {
      text-align: center;
    }
    @media (min-width: $w-medium-b) {
      font-size: 32px;
      line-height: 40px;
    }
  }

  &__progress {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    max-width: var(--onboarding-inner-width);
    padding: 24px 20px;
    margin-bottom: 40px;
    border-radius: 12px;
    background: $c-white;
  }

  &__progress-bar {
    position: relative;
    width: 100%;
    height: 8px;
    border-radius: 8px;
    background: $c-border-7;

    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 0;
      border-radius: 99px;
      background: $c-yellow;
      width: var(--current-progress);
      transition: all 0.15s linear;
    }
  }

  &__progress-label {
    font-size: 20px;
    line-height: 22px;
    font-weight: 500;
    text-align: right;
    min-width: 4ch;
    color: $c-black-15;
  }

  &__steps {
    width: 100%;
    max-width: var(--onboarding-inner-width);
  }

  &__step-dot {
    z-index: 1;
    width: 9px;
    height: 9px;
    border-radius: 9px;
    flex-shrink: 0;
    background: currentColor;
  }

  &__step {
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    padding-block: 12px;
    color: $c-black-13;

    &:where(:not(:first-child)):before,
    &:where(:not(:last-child)):after {
      content: '';
      position: absolute;
      left: 4px;
      width: 1px;
      height: 50%;
      background: $c-black-13;
    }

    &:before {
      top: 0;
    }

    &:after {
      bottom: 0;
    }
  }

  &__step--current,
  &__step--prev {
    color: $c-yellow;
  }

  &__step--prev {
    &:before,
    &:after {
      background: $c-yellow;
    }
  }

  &__step--current {
    &:before {
      background: $c-yellow;
    }
  }

  &__step-title {
    margin: 0;
    width: 100%;
    font-size: 18px;
    line-height: 20px;
    letter-spacing: -0.02em;
    font-weight: 500;
    color: $c-black-2;
    padding-top: 2px;
    margin-bottom: -2px;
  }

  &__status--prev,
  &__status--next {
    flex-shrink: 0;
    width: 23px;
    height: 23px;
    margin-block: 2.5px;
    border-radius: 23px;
  }

  &__status--prev {
    display: flex;
    justify-content: center;
    align-items: center;
    background: $c-yellow;
  }

  &__status--next {
    border: 1px solid $c-black-13;
  }

  &__popup {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 999;
    width: 100vw;
    height: 100dvh;
    background: rgba($c-black-2, 0.3);
  }

  &__popup-inner {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    min-height: 264px;
    background: $c-white;
    padding-inline: 24px;
    padding-block: 40px 28px;
    border-radius: 14px 14px 0 0;
    @media (min-width: $w-tablet-b) {
      width: 600px;
      left: calc(50% - 300px);
    }
  }

  &__popup-label {
    font-size: 14px;
    line-height: 18px;
    text-align: center;
    margin-bottom: 8px;
    color: $c-black-3;
  }

  &__popup-title {
    font-size: 20px;
    line-height: 26px;
    letter-spacing: -0.02em;
    text-align: center;
    font-weight: 500;
    margin-bottom: 32px;
    color: $c-black-2;
  }

  &__popup-controls {
    display: flex;
    justify-content: center;
    gap: 18px;
    width: 100%;
  }

  &__popup-button {
    display: block;
    flex: 1;
    background: $c-bg;
    max-width: 150px;
  }

  &__reviews {
    position: absolute;
    left: 0;
    bottom: 0;
    display: flex;
    gap: 8px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-bottom: var(--onboarding-padding);
    width: 100%;
  }

  &__stars {
    max-width: 180px;
  }

  &__review {
    font-size: 24px;
    line-height: 30px;
    letter-spacing: -0.02em;
    font-weight: 500;
    color: $c-black-2;
  }
}

.popup-pop {
  &-enter-active,
  &-leave-active {
    transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);

    .onboarding-finishing__popup-inner {
      transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    }
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;

    .onboarding-finishing__popup-inner {
      transform: translateY(100%);
    }
  }

  &-enter-to,
  &-leave-from {
    opacity: 1;

    .onboarding-finishing__popup-inner {
      transform: translateY(0%);
    }
  }
}

.swap-state {
  &-enter-active,
  &-leave-active {
    transition: all 0.3s ease;

    .onboarding-finishing__step-title,
    .onboarding-finishing__status {
      transition: all 0.3s ease;
    }
  }

  &-enter-from,
  &-leave-to {
    .onboarding-finishing__step-title,
    .onboarding-finishing__status {
      opacity: 0;
    }
  }

  &-enter-to,
  &-leave-from {
    .onboarding-finishing__step-title,
    .onboarding-finishing__status {
      opacity: 1;
    }
  }
}
</style>
